import { defineStore } from 'pinia';
import { defaultSensorViewValues } from '../models/sensorViewValues';
import type { SensorViewValues } from '../models/sensorViewValues';
import type { OrganizationForm } from '@nfc-api/user';
import type { UserCreationRequest } from '@nfc-api/config';
import { useTagApi, useUserApi, useAuthApi } from '@wision/api';
import type { ApiReturnType } from '@wision/api';
import { useAuth } from '#imports';
import { useNuxtApp } from '#app';

type DeviceConfig = {
  gpsLat: number,
  gpsLong: number,
  locationName: string,
  locationDescription: string,
  deviceName: string
}

type RootState = {
  tag?: ApiReturnType<typeof tagApi.getTag>;
  meta?: ApiReturnType<typeof tagApi.getTagMeta>;
  hasCheckedSession: boolean;
  viewValues: SensorViewValues;
  selectedOrganizationId: number | undefined;
  createdOrganization: OrganizationForm | undefined;
  createdUser: UserCreationRequest | undefined;
  termsAcceptet: boolean;
  deviceConfig: DeviceConfig | undefined;
  configTag?: ApiReturnType<typeof tagApi.getConfigTag>;
  avatar: string | undefined;
  access: {
    name: string;
    customers: Array<{id: number, name: string}>
  } | undefined
}

const tagApi = useTagApi('/api/proxy');
const userApi = useUserApi('/api/proxy');
const authApi = useAuthApi('/api/proxy');

const MAX_SEND_RETRIES = 3;
const SEND_RETRY_DELAY = 2000;

export const useAppState = defineStore('onboarding', {
  state: () => ({
    userConfig: {},
    tag: undefined,
    user: undefined,
    access: undefined,
    hasCheckedSession: false,
    organization: undefined,
    viewValues: defaultSensorViewValues,
    createdOrganization: undefined,
    selectedOrganizationId: undefined,
    createdUser: undefined,
    termsAcceptet: false,
    deviceConfig: undefined,
    configTag: undefined,
    avatar: undefined
  } as RootState),

  actions: {
    setCreatedOrganization(organization: OrganizationForm | undefined) {
      this.createdOrganization = organization;
    },
    setCreatedUser(user: UserCreationRequest) {
      this.createdUser = user;
    },
    setTermsAcceptet(acceptet: boolean) {
      this.termsAcceptet = acceptet;
    },
    setDeviceConfig(deviceConfig: DeviceConfig) {
      this.deviceConfig = deviceConfig;
    },
    setSelectedOrgId(id: number) {
      this.selectedOrganizationId = id;
    },

    async fetchConfigTag(uuid: string) {
      const [tag, error] = await tagApi.getConfigTag(uuid);
      if (error && tag !== null) return;

      this.configTag = tag;
    },
    /**
     * Fetch tag
     */
    async fetchTag(uuid: string) {
      const [tag, error, , statusCode] = await tagApi.getTag(uuid);
      if (error && tag !== null) return { tag: undefined, statusCode };

      this.tag = tag;

      return { tag, statusCode };
    },
    async fetchLinkTag(uuid: string) {
      return await tagApi.getLinkTag(uuid);
    },
    /**
     * Fetch tag meta
     */
    async fetchTagMeta(uuid: string) {
      if (this.meta) return;

      const [meta, error] = await tagApi.getTagMeta(uuid);
      if (error && meta !== null) return;

      this.meta = meta;

      return meta;
    },
    async fetchDeviceTag(uuid: string) {
      const [tag, error] = await tagApi.getDeviceTag(uuid);
      if (error && tag !== null) return;

      const sensor = tag?.payload.sensor;
      if (!sensor) return;

      const deviceId = sensor.id;

      const battery = sensor.values.find(v => v.type === 'battery');
      const values = sensor
        ? sensor.values
          .filter(v => v.type !== 'battery')
          .map((m) => (
            {
              value: m.value,
              unit: m.unit,
              type: m.type,
              name: m.name,
              index: m.index,
              channel: m.channel,
            }
          ))
        : [];

      this.viewValues = {
        name: sensor.name,
        address: sensor.locationDescription,
        updated: new Date(sensor.timeStamp as string)?.toLocaleString(),
        battery: battery?.value ?? 0,
        batteryUnit: battery?.unit ?? '',
        serial: sensor.serial ?? '12E1234B54321',
        deviceId,
        batch: 'X2382320',
        template: tag?.payload.template,
        values,
      };
    },
    async createAccount() {
      if (!this.$state.createdOrganization || !this.$state.createdUser) return;

      const { name, email, phone, password } = this.$state.createdUser;
      const [, error] = await tagApi.createAccount(
        this.$state.configTag?.uuid as string,
        {
          username: email,
          name,
          email,
          password,
          phone,
          organizations: []
        },
        this.$state.createdOrganization
      );

      if (!error) {
        this.setCreatedUser({
          email,
          name: '',
          password: '',
          phone: ''
        });

        this.setCreatedOrganization(undefined);
      }
      console.info(error, 'error');
      return !error;
    },

    async authorizeSession() {
      const { $orpc } = useNuxtApp();
      const { data, getSession } = useAuth();

      try {
        const userAccess = await $orpc.auth.getUserAccess();
        await getSession();
        this.access = {
          name: data.value?.user?.name ?? '',
          customers: userAccess.access
            .filter((a): a is typeof a & { xtoolId: number } => a.xtoolId !== undefined)
            .map((a) => ({
              id: a.xtoolId,
              name: a.name,
            })),
        };
        this.avatar = data.value?.user?.image ?? undefined;
      } catch {
        this.access = undefined;
      }
      this.hasCheckedSession = true;
    },

    /**
     * Login
     */
    async login() {
      const { signIn } = useAuth();
      await signIn('zitadel', { redirect: true });
    },
    /**
     * Logout
     */
    async logout(callbackUrl = '/login') {
      const { signOut } = useAuth();
      this.access = undefined;
      await signOut({ callbackUrl });
    },

    /**
     * Send the user configuration to the backend
     */
    async sendUserConfig(uuid: string): Promise<boolean> {
      for (let retries = 0; retries < MAX_SEND_RETRIES; retries++) {
        console.log(`[sendUserConfig] Attempt ${retries + 1}/${MAX_SEND_RETRIES}`);

        const [result, error] = await tagApi.sendUserConfig(uuid, {
          ...this.$state.deviceConfig as DeviceConfig,
          existingOrganizationId: this.selectedOrganizationId
        });

        console.log('[sendUserConfig] Result:', result);
        console.log('[sendUserConfig] Error:', error);
        console.log('[sendUserConfig] Error is falsy:', !error);

        if (!error) {
          console.log('[sendUserConfig] Success! Returning true');
          return true;
        }

        console.log(`[sendUserConfig] Failed, error truthy. Will retry: ${retries < MAX_SEND_RETRIES - 1}`);

        if (retries < MAX_SEND_RETRIES - 1)
          await new Promise(resolve => setTimeout(resolve, SEND_RETRY_DELAY));
      }

      console.log('[sendUserConfig] All retries exhausted, returning false');
      return false;
    },
    async userExists(tagId: string, email: string) {
      return await tagApi.userExists(tagId, email);
    }
  },
  getters: {
    hasAccess: (state) => !!state?.access,
    userName: (state) => state.access ? state.access.name : '-',
    build: () => import.meta.env.VITE_WISION_BUILD_ID ?? '',
    unitName: (state) => (state?.configTag)?.config?.unitType,
    serial: (state) => (state?.configTag)?.config?.unitSerial,
  }
});
